/**
 * The room's list: the Dashboard, narrow, for switching while a table is open.
 *
 * THE SAME LIST AS THE DASHBOARD, drawn the same way: one row per view
 * (`views.dashboardTiles`), named and ordered as its card, and the views with
 * nothing in them yet under "Not started", as on the Dashboard. It listed every
 * raw table, so room 53's "Who covers it" showed here by name while the
 * Dashboard had it inside "Search interest".
 *
 * A view is on this list or it waits in All views - one place to look, where
 * there used to be the board, a "Not pinned" shelf and Browse. × takes one off
 * (it waits in All views). At the bottom, what the room spotted and every view
 * it can show, folded into three counts; each opens its panel over the board.
 */
import type { BoardroomDeliverable } from '../../../types';
import DeliverableTile from './DeliverableTile';
import type { DashboardTile } from './views';

export type BoardPanel = 'noticed' | 'observed' | 'add';

const BADGES: { key: BoardPanel; label: string; title: string }[] = [
  { key: 'observed', label: 'Observed', title: 'Findings and data problems the room saw across the board' },
  { key: 'add', label: 'Add a view', title: 'Put a view on the dashboard, ask the chat for one, or manage every table and card' },
];

/** Noticed, Observed and All views, each with its count; each opens its panel over
 *  the board. A column beside an open table, a row above the dashboard. */
export function BoardButtons({
  counts,
  panel,
  onPanel,
  row = false,
}: {
  counts: Record<BoardPanel, number>;
  panel: BoardPanel | null;
  onPanel: (p: BoardPanel | null) => void;
  row?: boolean;
}) {
  return (
    <div className={row ? 'flex flex-wrap items-center gap-2' : 'flex shrink-0 flex-col gap-1'} data-testid="board-buttons">
      {BADGES.map(b => {
          const n = counts[b.key];
          const on = panel === b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => onPanel(on ? null : b.key)}
              aria-pressed={on}
              title={b.title}
              data-testid={`board-badge-${b.key}`}
              className={`flex items-center justify-between gap-3 rounded-md border px-2.5 py-1.5 text-xs ${
                on
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-800 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-200'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600'
              } ${n || b.key === 'add' ? '' : 'opacity-60'}`}
            >
              <span>{b.label}</span>
              {/* "Add a view" is an action, not a pile: no count. */}
              {b.key !== 'add' && (
                <span
                  className={`rounded-full px-1.5 text-[11px] tabular-nums ${
                    n
                      ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
    </div>
  );
}

export default function BoardList({
  tiles,
  isEmpty,
  open,
  badges,
  matchesOf,
  onOpen,
  onRemove,
  counts,
  panel,
  onPanel,
  glowing,
  className = '',
}: {
  /** The Dashboard's tiles, in its order - plus the open table when it is not on it. */
  tiles: DashboardTile[];
  /** Nothing in it yet: such a view goes under "Not started". */
  isEmpty: (d: BoardroomDeliverable) => boolean;
  open: BoardroomDeliverable | null;
  badges?: Record<string, number>;
  matchesOf: (d: BoardroomDeliverable) => { hits: number; seen: number } | null;
  onOpen: (d: BoardroomDeliverable) => void;
  /** Take a view off the Dashboard: every table of it. */
  onRemove: (members: BoardroomDeliverable[]) => void;
  counts: Record<BoardPanel, number>;
  panel: BoardPanel | null;
  onPanel: (p: BoardPanel | null) => void;
  /** Just added: these glow for a few seconds. */
  glowing?: ReadonlySet<number>;
  className?: string;
}) {
  const empty = (t: DashboardTile) => t.members.every(isEmpty);
  const started = tiles.filter(t => !empty(t));
  const notStarted = tiles.filter(empty);
  const row = (t: DashboardTile) => {
    // The face is the Dashboard card's: the view's first table with something in it.
    const face = t.members.find(m => !isEmpty(m)) ?? t.lead;
    const selected = !!open && t.members.some(m => m.id === open.id);
    const pinned = t.members.filter(m => m.pinned);
    const badge = t.members.reduce((n, m) => n + (badges?.[m.kind] ?? 0), 0);
    return (
      <DeliverableTile
        key={face.id}
        deliverable={face}
        title={t.view?.label}
        badge={badge || undefined}
        selected={selected}
        glow={t.members.some(m => glowing?.has(m.id))}
        fill
        matches={matchesOf(face)}
        onOpen={() => !selected && onOpen(face)}
        // An open table that is not on the Dashboard shows here marked, with
        // nothing to take off.
        onPin={pinned.length ? () => onRemove(pinned) : undefined}
      />
    );
  };
  return (
    <nav className={`flex min-h-0 flex-col gap-2 ${className}`} data-testid="board-list">
      {/* AT THE TOP, so they are always on screen: pinned to the foot of a
          column taller than the window, they sat below the fold. */}
      {/* A LITTLE SPACE AND A LINE between the buttons and the tables, so the
          buttons do not read as three more entries on the list. */}
      <div className="shrink-0 border-b border-gray-200 pb-3 mb-1 dark:border-gray-700">
        <BoardButtons counts={counts} panel={panel} onPanel={onPanel} />
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden py-0.5 pl-0.5 pr-2 [scrollbar-width:thin]">
        {tiles.length === 0 && (
          <p className="px-1 text-xs text-gray-500 dark:text-gray-400">
            Nothing pinned yet.{' '}
            <button type="button" onClick={() => onPanel('add')} className="text-indigo-600 hover:underline dark:text-indigo-300">
              Add a view
            </button>
          </p>
        )}
        {started.map(row)}
        {notStarted.length > 0 && (
          <div className="pt-1" data-testid="board-list-not-started">
            <div className="px-1 pb-1 text-[11px] text-gray-500 dark:text-gray-400">Not started</div>
            <div className="space-y-2 opacity-70">{notStarted.map(row)}</div>
          </div>
        )}
      </div>
    </nav>
  );
}
